import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePublicBranches } from "@/hooks/usePublicBranches";
import { MapPin, Phone, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export default function BranchesPage() {
  const { t, language } = useLanguage();
  const { branches, loading } = usePublicBranches();

  const loc = (en: string, ar: string) => (language === "ar" && ar ? ar : en);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-12">
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground mb-2">
            {t("branches.title")}
          </h1>
          <p className="text-muted-foreground mb-8">{t("branches.subtitle")}</p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="rounded-xl border bg-card animate-pulse h-40" />
              ))}
            </div>
          ) : branches.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-40" />
              <p>{t("branches.empty")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {branches.map((branch) => (
                <Card key={branch.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                      {loc(branch.name, branch.nameAr)}
                    </h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {(branch.address || branch.addressAr) && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                          <span>{loc(branch.address, branch.addressAr)}</span>
                        </div>
                      )}
                      {branch.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-primary shrink-0" />
                          <a href={`tel:${branch.phone}`} className="hover:text-primary transition-colors">
                            {branch.phone}
                          </a>
                        </div>
                      )}
                      {(branch.workingHours || branch.workingHoursAr) && (
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                          <span>{loc(branch.workingHours, branch.workingHoursAr)}</span>
                        </div>
                      )}
                    </div>
                    {branch.latitude && branch.longitude && (
                      <a
                        href={`https://www.google.com/maps?q=${branch.latitude},${branch.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-primary hover:underline"
                      >
                        <MapPin className="h-4 w-4" />
                        {t("branches.viewMap")}
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
